import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  LinearProgress,
  TextField,
  MenuItem,
  IconButton,
  Tooltip,
  Button,
} from '@mui/material';
import {
  AccountBalance as TaxIcon,
  Receipt as GSTIcon,
  Assignment as TDSIcon,
  Assessment as ReportIcon,
  Refresh,
} from '@mui/icons-material';
import { complianceService, ComplianceDashboard } from '../../services/complianceService';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`compliance-tabpanel-${index}`}
      aria-labelledby={`compliance-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const formatCurrency = (value: number) => {
  return `₹${value.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

const CompliancePage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [dashboardData, setDashboardData] = useState<ComplianceDashboard | null>(null);

  const now = new Date();
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());

  const fetchDashboard = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response: any = await complianceService.getDashboard({ month: selectedMonth, year: selectedYear });
      setDashboardData(response);
    } catch (err: any) {
      setError(err?.message || 'Failed to load compliance data');
    } finally {
      setLoading(false);
    }
  }, [selectedMonth, selectedYear]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" action={
          <Tooltip title="Retry">
            <IconButton size="small" onClick={fetchDashboard}><Refresh /></IconButton>
          </Tooltip>
        }>
          {error}
        </Alert>
      </Box>
    );
  }

  const gst = dashboardData?.gstSummary;
  const tds = dashboardData?.tdsSummary;
  const score = dashboardData?.complianceScore;
  const activities = dashboardData?.recentActivities || [];

  const getComplianceColor = (val: number): 'success' | 'warning' | 'error' => {
    if (val >= 90) return 'success';
    if (val >= 75) return 'warning';
    return 'error';
  };

  const months = [
    { value: 1, label: 'January' }, { value: 2, label: 'February' }, { value: 3, label: 'March' },
    { value: 4, label: 'April' }, { value: 5, label: 'May' }, { value: 6, label: 'June' },
    { value: 7, label: 'July' }, { value: 8, label: 'August' }, { value: 9, label: 'September' },
    { value: 10, label: 'October' }, { value: 11, label: 'November' }, { value: 12, label: 'December' },
  ];

  const years = Array.from({ length: 5 }, (_, i) => now.getFullYear() - i);

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2} mb={3}>
        <Box>
          <Typography variant="h4" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
            Indian Compliance Dashboard
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Comprehensive overview of GST, TDS, and regulatory compliance status.
          </Typography>
        </Box>
        <Box display="flex" gap={2} alignItems="center">
          <TextField
            select
            size="small"
            label="Month"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(Number(e.target.value))}
            sx={{ minWidth: 130 }}
          >
            {months.map(m => (
              <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label="Year"
            value={selectedYear}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
            sx={{ minWidth: 100 }}
          >
            {years.map(y => (
              <MenuItem key={y} value={y}>{y}</MenuItem>
            ))}
          </TextField>
          <Tooltip title="Refresh">
            <IconButton onClick={fetchDashboard} color="primary">
              <Refresh />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Compliance Overview Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, md: 3 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <TaxIcon color="primary" sx={{ mr: 2, fontSize: 32 }} />
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="h4" color="primary.main">
                    {score?.overall ?? 0}%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Overall Compliance
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={score?.overall ?? 0}
                    sx={{ mt: 1 }}
                    color={getComplianceColor(score?.overall ?? 0)}
                  />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 3 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <GSTIcon color="success" sx={{ mr: 2, fontSize: 32 }} />
                <Box>
                  <Typography variant="h4" color="success.main">
                    {formatCurrency(gst?.netLiability ?? 0)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Net GST Liability
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {gst?.salesInvoiceCount ?? 0} invoices
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 3 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <TDSIcon color="warning" sx={{ mr: 2, fontSize: 32 }} />
                <Box>
                  <Typography variant="h4" color="warning.main">
                    {formatCurrency(tds?.pendingDeposit ?? 0)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Pending TDS Deposit
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {tds?.deductionCount ?? 0} deductions
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 3 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <ReportIcon color="info" sx={{ mr: 2, fontSize: 32 }} />
                <Box>
                  <Typography variant="h4" color="info.main">
                    {tds?.challanCount ?? 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    TDS Challans Filed
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Deposited: {formatCurrency(tds?.totalDeposited ?? 0)}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Compliance Tabs */}
      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="compliance tabs">
            <Tab label="GST Summary" />
            <Tab label="TDS Summary" />
            <Tab label="Compliance Score" />
            <Tab label="Recent Activities" />
          </Tabs>
        </Box>

        {/* GST Summary Tab */}
        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="h6" gutterBottom color="primary">
                GST Collected (Output Tax)
              </Typography>
              <TableContainer component={Paper} elevation={0}>
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell>CGST Collected</TableCell>
                      <TableCell align="right">{formatCurrency(gst?.collected.cgst ?? 0)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>SGST Collected</TableCell>
                      <TableCell align="right">{formatCurrency(gst?.collected.sgst ?? 0)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>IGST Collected</TableCell>
                      <TableCell align="right">{formatCurrency(gst?.collected.igst ?? 0)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell><strong>Total GST Collected</strong></TableCell>
                      <TableCell align="right"><strong>{formatCurrency(gst?.collected.total ?? 0)}</strong></TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Sales Invoices</TableCell>
                      <TableCell align="right">{gst?.salesInvoiceCount ?? 0}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="h6" gutterBottom color="primary">
                Input Tax Credit & Net Liability
              </Typography>
              <TableContainer component={Paper} elevation={0}>
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell>CGST Input Credit</TableCell>
                      <TableCell align="right">{formatCurrency(gst?.itc.cgst ?? 0)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>SGST Input Credit</TableCell>
                      <TableCell align="right">{formatCurrency(gst?.itc.sgst ?? 0)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>IGST Input Credit</TableCell>
                      <TableCell align="right">{formatCurrency(gst?.itc.igst ?? 0)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell><strong>Total Input Credit</strong></TableCell>
                      <TableCell align="right"><strong>{formatCurrency(gst?.itc.total ?? 0)}</strong></TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell><strong>Net GST Liability</strong></TableCell>
                      <TableCell align="right" sx={{ color: 'error.main' }}>
                        <strong>{formatCurrency(gst?.netLiability ?? 0)}</strong>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Purchase Invoices</TableCell>
                      <TableCell align="right">{gst?.purchaseInvoiceCount ?? 0}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>
        </TabPanel>

        {/* TDS Summary Tab */}
        <TabPanel value={tabValue} index={1}>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="h6" gutterBottom color="primary">
                TDS Deduction & Deposit Summary
              </Typography>
              <TableContainer component={Paper} elevation={0}>
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell><strong>Total TDS Deducted</strong></TableCell>
                      <TableCell align="right"><strong>{formatCurrency(tds?.totalDeducted ?? 0)}</strong></TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell><strong>Total TDS Deposited</strong></TableCell>
                      <TableCell align="right">{formatCurrency(tds?.totalDeposited ?? 0)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell><strong>Pending Deposit</strong></TableCell>
                      <TableCell align="right" sx={{ color: 'warning.main' }}>
                        <strong>{formatCurrency(tds?.pendingDeposit ?? 0)}</strong>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Total Deductions</TableCell>
                      <TableCell align="right">{tds?.deductionCount ?? 0}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Challans Filed</TableCell>
                      <TableCell align="right">{tds?.challanCount ?? 0}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              {(tds?.pendingDeposit ?? 0) > 0 && (
                <Alert
                  severity="warning"
                  sx={{ mb: 2 }}
                  action={
                    <Button color="inherit" size="small" onClick={() => navigate('/finance/tds-reports')}>
                      View TDS
                    </Button>
                  }
                >
                  <Typography variant="body2">
                    <strong>Action Required:</strong> {formatCurrency(tds?.pendingDeposit ?? 0)} TDS is pending deposit.
                    Please deposit before the due date to avoid penalties.
                  </Typography>
                </Alert>
              )}

              {(tds?.pendingDeposit ?? 0) === 0 && (tds?.deductionCount ?? 0) > 0 && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    All TDS deductions have been deposited for this period.
                  </Typography>
                </Alert>
              )}

              {(tds?.deductionCount ?? 0) === 0 && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    No TDS deductions recorded for this period.
                  </Typography>
                </Alert>
              )}
            </Grid>
          </Grid>
        </TabPanel>

        {/* Compliance Score Tab */}
        <TabPanel value={tabValue} index={2}>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="h6" gutterBottom color="primary">
                Compliance Scores
              </Typography>

              <Box sx={{ mb: 3 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="body1">GST Filing</Typography>
                  <Typography variant="body1" fontWeight="bold">{score?.gstFiling ?? 0}%</Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={score?.gstFiling ?? 0}
                  color={getComplianceColor(score?.gstFiling ?? 0)}
                  sx={{ mt: 1, mb: 2 }}
                />
              </Box>

              <Box sx={{ mb: 3 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="body1">TDS Compliance</Typography>
                  <Typography variant="body1" fontWeight="bold">{score?.tdsCompliance ?? 0}%</Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={score?.tdsCompliance ?? 0}
                  color={getComplianceColor(score?.tdsCompliance ?? 0)}
                  sx={{ mt: 1, mb: 2 }}
                />
              </Box>

              <Box sx={{ mb: 3 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="body1">Invoice Completeness</Typography>
                  <Typography variant="body1" fontWeight="bold">{score?.invoiceCompleteness ?? 0}%</Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={score?.invoiceCompleteness ?? 0}
                  color={getComplianceColor(score?.invoiceCompleteness ?? 0)}
                  sx={{ mt: 1, mb: 2 }}
                />
              </Box>

              <Box sx={{ mb: 3 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="body1" fontWeight="bold">Overall Score</Typography>
                  <Typography variant="body1" fontWeight="bold">{score?.overall ?? 0}%</Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={score?.overall ?? 0}
                  color={getComplianceColor(score?.overall ?? 0)}
                  sx={{ mt: 1, mb: 2, height: 8, borderRadius: 4 }}
                />
              </Box>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="h6" gutterBottom color="primary">
                Recommendations
              </Typography>

              {(score?.gstFiling ?? 0) >= 90 && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    <strong>GST Filing ({score?.gstFiling}%)</strong> - Your GST compliance is excellent.
                    Keep maintaining accurate records.
                  </Typography>
                </Alert>
              )}
              {(score?.gstFiling ?? 0) < 90 && (score?.gstFiling ?? 0) >= 75 && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    <strong>GST Filing ({score?.gstFiling}%)</strong> - Some invoices may be missing GST details.
                    Review and ensure all invoices have proper GST information.
                  </Typography>
                </Alert>
              )}
              {(score?.gstFiling ?? 0) < 75 && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    <strong>GST Filing ({score?.gstFiling}%)</strong> - Multiple invoices are missing GST details.
                    Immediate attention required.
                  </Typography>
                </Alert>
              )}

              {(score?.tdsCompliance ?? 0) < 90 && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    <strong>TDS Compliance ({score?.tdsCompliance}%)</strong> - Some TDS deductions are pending deposit.
                    Timely deposit avoids interest and penalties.
                  </Typography>
                </Alert>
              )}
              {(score?.tdsCompliance ?? 0) >= 90 && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    <strong>TDS Compliance ({score?.tdsCompliance}%)</strong> - TDS deposits are on track.
                  </Typography>
                </Alert>
              )}

              {(score?.invoiceCompleteness ?? 0) < 90 && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    <strong>Invoice Completeness ({score?.invoiceCompleteness}%)</strong> - Some invoices are missing
                    customer GSTIN. Ensure B2B invoices have valid GSTIN for ITC claims.
                  </Typography>
                </Alert>
              )}
            </Grid>
          </Grid>
        </TabPanel>

        {/* Recent Activities Tab */}
        <TabPanel value={tabValue} index={3}>
          <Typography variant="h6" gutterBottom color="primary">
            Recent Compliance Activities
          </Typography>
          {activities.length === 0 ? (
            <Alert severity="info">No recent compliance activities found.</Alert>
          ) : (
            <TableContainer component={Paper} elevation={0}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Module</TableCell>
                    <TableCell>Description</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {activities.map((activity, index) => (
                    <TableRow key={activity.id || index}>
                      <TableCell>
                        {new Date(activity.timestamp).toLocaleDateString('en-IN')}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={activity.type}
                          size="small"
                          color={
                            activity.type.includes('create') ? 'success' :
                            activity.type.includes('update') ? 'info' :
                            'default'
                          }
                        />
                      </TableCell>
                      <TableCell>{activity.module}</TableCell>
                      <TableCell>{activity.description}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>
      </Card>
    </Box>
  );
};

export default CompliancePage;
